import type { PermissionId, RTCIceServerLike, User } from "./types";

/** Nome do canal padrao aberto ao entrar. */
export const DEFAULT_CHANNEL_ID = "geral";

/**
 * Timeout expira sozinho: só compara com a hora atual, sem job/cron
 * separado. Compartilhado entre cliente e servidor para as duas pontas
 * nunca divergirem sobre quem está em timeout agora.
 */
export function isTimedOut(user: Pick<User, "timeoutUntil"> | null | undefined): boolean {
  return user != null && user.timeoutUntil !== null && user.timeoutUntil > Date.now();
}

/** Chave canonica de uma conversa direta (independe de quem abriu). */
export function dmKey(userA: string, userB: string): string {
  return `dm:${[userA, userB].sort().join("|")}`;
}

/** Chave de um canal de texto. */
export function channelKey(channelId: string): string {
  return `ch:${channelId}`;
}

/** Extrai o outro participante de uma chave de DM, ou `null` se nao for DM. */
export function dmPartner(key: string, meId: string): string | null {
  if (!key.startsWith("dm:")) return null;
  const parts = key.slice(3).split("|");
  if (parts.length !== 2 || !parts.includes(meId)) return null;
  return parts[0] === meId ? parts[1] : parts[0];
}

export const PERMISSIONS: { id: PermissionId; name: string; desc: string }[] = [
  { id: "canais", name: "Gerenciar canais", desc: "Criar, editar e remover canais" },
  { id: "cargos", name: "Gerenciar cargos", desc: "Editar cargos e permissoes" },
  { id: "expulsar", name: "Expulsar membros", desc: "Remover membros da comunidade" },
  { id: "silenciar", name: "Silenciar membros", desc: "Silenciar nas salas de voz" },
  { id: "mensagens", name: "Enviar mensagens", desc: "Publicar nos canais de texto" },
  { id: "voz", name: "Conectar em voz", desc: "Entrar nas salas de voz" },
  { id: "tela", name: "Compartilhar tela", desc: "Transmitir tela nas salas" },
  { id: "todos", name: "Mencionar @todos", desc: "Notificar todos os membros" },
];

/**
 * Cores de cargo. As duas primeiras sao as da marca (o azul do balao e o azul
 * claro do losango); as outras existem para um servidor conseguir diferenciar
 * cargos sem sair de uma paleta que combina com o app.
 */
export const ROLE_SWATCHES = ["#1F4FD8", "#4CA9FF", "#7C5CFF", "#FF4D6D", "#9BAAD0"] as const;

/** Cores de acento oferecidas nas configuracoes de cada servidor. */
export const ACCENT_SWATCHES = ["#1F4FD8", "#4CA9FF", "#7C5CFF", "#FF4D6D"] as const;

/* ---------------------------------------------------------------------- */
/* Qualidade de video                                                      */
/* ---------------------------------------------------------------------- */

export type QualityId =
  | "auto"
  | "480p30"
  | "720p30"
  | "720p60"
  | "1080p30"
  | "1080p60"
  | "1440p60"
  | "2160p60";

export type QualityPreset = {
  id: QualityId;
  label: string;
  /** Altura alvo em pixels; `null` deixa a rede decidir. */
  height: number | null;
  width: number | null;
  frameRate: number | null;
  /** Teto de bitrate do encoder, em bits por segundo. */
  maxBitrate: number | null;
  /** Bitrate inicial sugerido ao Chrome via SDP (kbps). */
  startBitrateKbps: number | null;
};

/**
 * Escada de qualidade. O padrao da transmissao de tela e 1080p60: altura 1080,
 * 60 quadros por segundo e teto de 12 Mbps, com `maintain-framerate` para que o
 * navegador prefira derrubar resolucao a derrubar quadros.
 */
export const QUALITY_PRESETS: Record<QualityId, QualityPreset> = {
  auto: {
    id: "auto",
    label: "Automatica - adaptativa",
    height: null,
    width: null,
    frameRate: null,
    maxBitrate: null,
    startBitrateKbps: null,
  },
  "480p30": {
    id: "480p30",
    label: "480p - 30 fps",
    height: 480,
    width: 854,
    frameRate: 30,
    maxBitrate: 1_200_000,
    startBitrateKbps: 600,
  },
  "720p30": {
    id: "720p30",
    label: "720p - 30 fps",
    height: 720,
    width: 1280,
    frameRate: 30,
    maxBitrate: 2_500_000,
    startBitrateKbps: 1_200,
  },
  "720p60": {
    id: "720p60",
    label: "720p - 60 fps",
    height: 720,
    width: 1280,
    frameRate: 60,
    maxBitrate: 4_500_000,
    startBitrateKbps: 2_000,
  },
  "1080p30": {
    id: "1080p30",
    label: "1080p - 30 fps",
    height: 1080,
    width: 1920,
    frameRate: 30,
    maxBitrate: 6_000_000,
    startBitrateKbps: 2_500,
  },
  "1080p60": {
    id: "1080p60",
    label: "1080p - 60 fps",
    height: 1080,
    width: 1920,
    frameRate: 60,
    maxBitrate: 12_000_000,
    startBitrateKbps: 5_000,
  },
  "1440p60": {
    id: "1440p60",
    label: "1440p - 60 fps",
    height: 1440,
    width: 2560,
    frameRate: 60,
    maxBitrate: 20_000_000,
    startBitrateKbps: 8_000,
  },
  "2160p60": {
    id: "2160p60",
    label: "4K - 60 fps",
    height: 2160,
    width: 3840,
    frameRate: 60,
    maxBitrate: 40_000_000,
    startBitrateKbps: 12_000,
  },
};

export const QUALITY_ORDER: QualityId[] = [
  "auto",
  "480p30",
  "720p30",
  "720p60",
  "1080p30",
  "1080p60",
  "1440p60",
  "2160p60",
];

/** Padrao da transmissao de tela. */
export const DEFAULT_SHARE_QUALITY: QualityId = "1080p60";

/** Padrao da webcam (mais leve que a tela). */
export const DEFAULT_CAM_QUALITY: QualityId = "720p30";

/** Opus: audio da transmissao em estereo de alta taxa. */
export const OPUS_SHARE_BITRATE = 256_000;

/** Opus: microfone em mono, com correcao de erro embutida. */
export const OPUS_MIC_BITRATE = 96_000;

/**
 * Codecs preferidos para transmissao de tela, em ordem.
 * VP9 vem primeiro por lidar melhor com conteudo de tela em 1080p60 no Chrome;
 * H264 entra em seguida pelo suporte a aceleracao por hardware.
 */
export const PREFERRED_VIDEO_CODECS = ["video/VP9", "video/H264", "video/AV1", "video/VP8"];

/** Dica de conteudo do track: movimento (jogos) ou nitidez (texto/codigo). */
export type ContentHintMode = "motion" | "detail";

export const DEFAULT_ICE_SERVERS: RTCIceServerLike[] = [
  { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] },
  { urls: ["stun:stun.cloudflare.com:3478"] },
];

/** Limite de caracteres de uma mensagem. */
export const MAX_MESSAGE_LENGTH = 2000;

/** Quantidade de mensagens devolvidas na primeira carga do historico. */
export const HISTORY_LIMIT = 200;

/**
 * Quantas mensagens sobem por vez ao rolar para tras.
 *
 * O historico deixou de ser "as ultimas 200 e acabou": cada pedido leva um
 * cursor e o servidor devolve a pagina anterior a ele, no mesmo desenho do
 * `before` do Discord. 50 e o passo — grande o bastante para a rolagem nao
 * pedir a cada dois dedos, pequeno o bastante para chegar rapido.
 */
export const HISTORY_PAGE = 50;

/** Teto por pedido, para um cliente adulterado nao pedir o canal inteiro. */
export const HISTORY_MAX = 100;

/* ---------------------------------------------------------------------- */
/* Orcamento de eventos do socket                                          */
/* ---------------------------------------------------------------------- */

/**
 * Teto de eventos por conexao, na mesma forma do gateway do Discord: 120
 * eventos a cada 60 segundos, quem passa cai fora.
 *
 * O que existia antes era so a fila serial por conexao — ela poe os eventos
 * em ordem, mas nao recusa nenhum: uma aba adulterada mandando `chat:send`
 * em laco enfileirava gravacao no banco tao rapido quanto a rede aguentasse,
 * e a fila crescia junto.
 */
export const GATEWAY_EVENTS = 120;
export const GATEWAY_WINDOW_MS = 60_000;

/**
 * A sinalizacao da malha WebRTC e a deteccao de fala tem orcamento proprio,
 * e maior: numa sala cheia o ICE trickle sozinho passa de dois eventos por
 * segundo, e derrubar quem esta em chamada por causa disso seria trocar um
 * problema que ninguem tinha por um que todo mundo teria.
 */
export const GATEWAY_MEDIA_EVENTS = 600;

/** Eventos que correm no orcamento de midia. */
export const GATEWAY_MEDIA_SET = new Set(["rtc:signal", "voice:state"]);

/** Mensagens enviadas: 10 a cada 10 segundos, por conexao. */
export const SEND_BURST = 10;
export const SEND_WINDOW_MS = 10_000;

/**
 * Compara duas versoes "a.b.c". Positivo quando A e mais nova, 0 quando sao
 * a mesma, negativo quando A e mais velha.
 *
 * Existe porque comparar versao como TEXTO da errado no primeiro numero de
 * dois digitos: "1.2.10" e diferente de "1.2.9", e a tela concluia que havia
 * atualizacao pendente para quem ja estava na mais nova. Alfabeticamente
 * "1.2.10" ate vem ANTES de "1.2.9", que e o oposto da verdade.
 *
 * Parte que nao for numero vale 0, e versao mais curta e completada com zeros:
 * "1.3" e a mesma coisa que "1.3.0".
 */
export function compareVersions(a: string, b: string): number {
  const parte = (v: string, i: number): number => {
    const n = Number.parseInt(v.split(".")[i] ?? "0", 10);
    return Number.isFinite(n) ? n : 0;
  };
  const casas = Math.max(a.split(".").length, b.split(".").length);
  for (let i = 0; i < casas; i += 1) {
    const diferenca = parte(a, i) - parte(b, i);
    if (diferenca !== 0) return diferenca;
  }
  return 0;
}

export const SESSION_STORAGE_KEY = "lfrcord.session";

/* ---------------------------------------------------------------------- */
/* Contas                                                                  */
/* ---------------------------------------------------------------------- */

/** Regras de cadastro, iguais no cliente e no servidor. */
export const HANDLE_PATTERN = /^[a-z0-9._-]{3,24}$/;
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_NAME_LENGTH = 32;
export const MAX_BIO_LENGTH = 280;
/** Status customizado, tipo "jogando X" — mais curto que a bio, sempre uma linha só. */
export const MAX_STATUS_LENGTH = 60;

/** Normaliza o handle digitado: minusculo, sem arroba e sem espacos. */
export function normalizeHandle(raw: string): string {
  return raw.trim().toLowerCase().replace(/^@+/, "").replace(/\s+/g, "");
}

export function handleError(raw: string): string | null {
  const handle = normalizeHandle(raw);
  if (!handle) return "Escolha um nome de usuario.";
  if (!HANDLE_PATTERN.test(handle)) {
    return "Use de 3 a 24 caracteres, apenas letras minusculas, numeros, ponto, hifen ou _.";
  }
  return null;
}

export function passwordError(password: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `A senha precisa de pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`;
  }
  return null;
}

export function nameError(raw: string): string | null {
  const name = raw.trim();
  if (!name) return "Escolha como voce quer aparecer.";
  if (name.length > MAX_NAME_LENGTH) return `No maximo ${MAX_NAME_LENGTH} caracteres.`;
  return null;
}

/* ---------------------------------------------------------------------- */
/* Verificacao de e-mail no cadastro                                       */
/* ---------------------------------------------------------------------- */

/** Checagem de formato, nao de entrega — quem confirma que o endereco existe e o codigo recebido. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emailError(raw: string): string | null {
  const email = raw.trim();
  if (!email) return "Informe seu e-mail.";
  if (email.length > 254 || !EMAIL_PATTERN.test(email)) return "Esse e-mail nao parece valido.";
  return null;
}

export const VERIFICATION_CODE_LENGTH = 6;
export const VERIFICATION_TTL_MS = 15 * 60 * 1000;
export const VERIFICATION_MAX_ATTEMPTS = 5;

/* ---------------------------------------------------------------------- */
/* Entrada com a conta do Google                                           */
/* ---------------------------------------------------------------------- */

/**
 * O callback do Google e uma navegacao do navegador, entao a sessao volta
 * pela URL. O que vai ali NAO e o token: e um codigo de uso unico, valido por
 * um minuto, que o cliente troca pelo token num POST. Assim o token de 30
 * dias nunca aparece no historico nem em log de proxy.
 */
export const GOOGLE_HANDOFF_PARAM = "glogin";

/** Motivo da recusa, quando o login com Google nao deu certo. */
export const GOOGLE_ERROR_PARAM = "gerror";

/** Esquema registrado pelo app desktop, usado pra devolver a sessao a ele. */
export const DESKTOP_PROTOCOL = "lfrcord";

/* ---------------------------------------------------------------------- */
/* Servidores e convites                                                   */
/* ---------------------------------------------------------------------- */

export const MAX_SERVER_NAME_LENGTH = 40;

export function serverNameError(raw: string): string | null {
  const name = raw.trim();
  if (!name) return "De um nome pro servidor.";
  if (name.length > MAX_SERVER_NAME_LENGTH) return `No maximo ${MAX_SERVER_NAME_LENGTH} caracteres.`;
  return null;
}

/** So maiusculas/digitos, sem caracteres ambiguos (0/O, 1/I/L) — mais facil de digitar/ditar. */
export const INVITE_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
export const INVITE_CODE_LENGTH = 8;
export const INVITE_CODE_PATTERN = /^[A-Z2-9]{6,8}$/;

export const MAX_INVITE_USES = 100_000;
/** 1 ano — maior que isso nao faz sentido pra convite "temporario". */
export const MAX_INVITE_TTL_MS = 365 * 24 * 60 * 60 * 1000;

/* ---------------------------------------------------------------------- */
/* Envio de arquivos                                                       */
/* ---------------------------------------------------------------------- */

/** Teto de upload: 100 MB por arquivo. */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

/** Avatares e icone da comunidade sao pequenos de proposito. */
export const MAX_AVATAR_BYTES = 8 * 1024 * 1024;

export const IMAGE_MIMES = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/avif"];
export const VIDEO_MIMES = ["video/mp4", "video/webm", "video/quicktime"];
export const AUDIO_MIMES = ["audio/mpeg", "audio/ogg", "audio/wav", "audio/webm"];

export const isImage = (mime: string) => IMAGE_MIMES.includes(mime);
export const isVideo = (mime: string) => VIDEO_MIMES.includes(mime);
export const isAudio = (mime: string) => AUDIO_MIMES.includes(mime);

/** Rotulo curto de tamanho, ex.: "12,4 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0).replace(".", ",")} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0).replace(".", ",")} MB`;
}

/* ---------------------------------------------------------------------- */
/* Mensagens                                                               */
/* ---------------------------------------------------------------------- */

/** Reacoes rapidas do seletor. */
export const QUICK_REACTIONS = ["👍", "😂", "🔥", "❤️", "😮", "😢", "🎯", "🏆"];

/** Enquanto o membro digita, o aviso vale por este tempo. */
export const TYPING_TTL_MS = 6_000;

/** Intervalo minimo entre dois avisos de digitacao do mesmo membro. */
export const TYPING_THROTTLE_MS = 2_500;

/** Extrai os handles mencionados com @ no corpo da mensagem. */
export function extractHandles(body: string): string[] {
  const encontrados = new Set<string>();
  const padrao = /(^|[^\w@])@([a-z0-9._-]{3,24})/gi;
  let achado: RegExpExecArray | null;
  while ((achado = padrao.exec(body))) encontrados.add(achado[2].toLowerCase());
  return [...encontrados];
}

/** Menção a todos, permitida so por quem tem a permissao `todos`. */
export const MENTION_EVERYONE = "todos";

export const MAX_SEARCH_RESULTS = 60;

/* ---------------------------------------------------------------------- */
/* Estrutura inicial da comunidade                                         */
/* ---------------------------------------------------------------------- */

export const DEFAULT_TEXT_CHANNELS = [
  { id: "geral", name: "geral", topic: "Avisos e conversa da crew" },
  { id: "jogos", name: "jogos", topic: "Comps, patches e horários" },
  { id: "clipes", name: "clipes", topic: "Melhores momentos da semana" },
];

export const DEFAULT_VOICE_CHANNELS = [
  { id: "lobby", name: "Lobby" },
  { id: "ranqueada", name: "Ranqueada" },
  { id: "afk", name: "AFK" },
];

/** Cargo recebido por quem se cadastra depois do fundador. */
export const DEFAULT_ROLE_ID = "crew";
export const OWNER_ROLE_ID = "fundador";

export const MAX_CHANNEL_NAME_LENGTH = 24;
export const MAX_TOPIC_LENGTH = 120;

/** Normaliza o nome de um canal de texto: minusculo, com hifens. */
export function normalizeChannelName(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "")
    .slice(0, MAX_CHANNEL_NAME_LENGTH);
}
